import React, { useEffect, useState } from "react";
import { ArrowLeft, Users, Trash2, Shield, User, Phone } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api } from "../api";
import { useAuth } from "../AuthContext";

const AdminUsers = () => {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null); // { id, name }
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/users");
      setUsers(r.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    load();
  }, []);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setError("");
    setDeleting(true);
    try {
      await api.delete(`/users/${deleteTarget.id}`);
      setDeleteTarget(null);
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Delete failed");
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#121212] pt-24 pb-20" data-testid="admin-users">
      <div className="max-w-5xl mx-auto px-6">
        <div className="flex items-center gap-4 mb-10">
          <button
            onClick={() => navigate("/admin")}
            className="flex items-center gap-2 text-white/40 hover:text-gold transition-colors text-xs font-body tracking-wider uppercase"
          >
            <ArrowLeft size={13} />
            <span>Dashboard</span>
          </button>
          <div className="h-px flex-1 bg-gold/10" />
        </div>

        <div className="flex items-center gap-3 mb-4">
          <div className="h-px w-8 bg-gold/60" />
          <span className="font-body text-gold text-[0.65rem] font-semibold tracking-[0.4em] uppercase">User Management</span>
        </div>
        <h1 className="font-display font-light text-5xl text-white mb-10">
          Users <span className="text-white/30 text-3xl">· {users.length}</span>
        </h1>

        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-sm px-4 py-3 mb-6" data-testid="users-error">
            <p className="font-body text-red-400 text-xs">{error}</p>
          </div>
        )}

        <div className="glass rounded-sm overflow-hidden">
          {loading ? (
            <div className="py-16 flex items-center justify-center">
              <div className="w-8 h-8 border-2 border-gold/20 border-t-gold rounded-full animate-spin" />
            </div>
          ) : users.length === 0 ? (
            <div className="py-16 text-center">
              <Users size={40} className="text-white/10 mx-auto mb-4" />
              <p className="font-body text-white/20 text-sm">No users found.</p>
            </div>
          ) : (
            <div className="divide-y divide-white/5">
              {users.map((u) => (
                <div key={u.id} className="flex items-center gap-4 px-6 py-4 hover:bg-white/[0.02] transition-colors" data-testid={`user-row-${u.id}`}>
                  <div className="w-10 h-10 rounded-sm border border-gold/20 flex items-center justify-center">
                    {u.role === "admin" ? <Shield size={15} className="text-gold" /> : <User size={15} className="text-white/40" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3">
                      <h3 className="font-display text-lg text-white">{u.name}</h3>
                      <span className="specialty-tag" style={{ fontSize: "0.55rem" }}>{u.role}</span>
                      {u.id === currentUser?.id && (
                        <span className="specialty-tag bg-gold/20 border-gold text-gold" style={{ fontSize: "0.55rem" }}>You</span>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-0.5">
                      <p className="font-body text-white/40 text-xs">{u.email}</p>
                      {u.phone && (
                        <p
                          className="font-body text-white/40 text-xs flex items-center gap-1.5"
                          data-testid={`user-phone-${u.id}`}
                        >
                          <Phone size={11} className="text-gold/50" />
                          <span>{u.phone}</span>
                        </p>
                      )}
                    </div>
                  </div>
                  <span className="font-body text-white/25 text-[0.65rem] tracking-wider hidden md:block">
                    Joined {new Date(u.created_at).toLocaleDateString()}
                  </span>
                  <button
                    onClick={() => setDeleteTarget({ id: u.id, name: u.name })}
                    disabled={u.id === currentUser?.id}
                    className="w-8 h-8 flex items-center justify-center rounded-sm border border-white/10 hover:border-red-500/50 hover:text-red-400 transition-all text-white/40 disabled:opacity-30 disabled:cursor-not-allowed"
                    title={u.id === currentUser?.id ? "Cannot delete your own account" : "Delete user"}
                    data-testid={`user-delete-${u.id}`}
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {deleteTarget && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-6"
          data-testid="user-delete-modal"
        >
          <div
            className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            onClick={() => !deleting && setDeleteTarget(null)}
          />
          <div className="relative glass-dark rounded-sm p-8 max-w-sm w-full">
            <h3 className="font-display text-2xl text-white mb-3">Confirm Delete</h3>
            <p className="font-body text-white/50 text-sm mb-6">
              Are you sure you want to remove{" "}
              <span className="text-gold">{deleteTarget.name}</span>? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={confirmDelete}
                disabled={deleting}
                className="flex-1 py-3 text-xs rounded-sm font-body font-semibold tracking-widest uppercase bg-red-500/20 border border-red-500/40 text-red-400 hover:bg-red-500/30 transition-colors disabled:opacity-60 disabled:cursor-wait flex items-center justify-center gap-2"
                data-testid="user-delete-confirm-yes"
              >
                {deleting ? (
                  <>
                    <span className="w-3 h-3 border border-red-400/40 border-t-red-400 rounded-full animate-spin" />
                    <span>Deleting…</span>
                  </>
                ) : (
                  <span>Delete</span>
                )}
              </button>
              <button
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
                className="flex-1 btn-outline-gold py-3 text-xs rounded-sm disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid="user-delete-confirm-cancel"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsers;
